import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { IUser } from 'src/utils/interfaces/user';
import { NotificationType } from 'src/components/ui/notification/Notification';
import { PaymentDataResponse } from 'src/services/models/payment.service';
import PaymentInformation from 'src/views/pages/payments/information/index';
import { PaymentsService } from 'src/services/payments.service';
import Spinner from 'src/components/ui/spinner';
import TooltipIcon from 'src/components/ui/tooltip';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    AccountType,
    Country,
    PaymentDataSearhType,
    PaymentSource,
} from 'src/utils/enums/payment.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import WarningNotification, {
    getWarningMessageKey,
} from '../ui/WarningNotification';

import './styles.scss';

export enum Tabs {
    PAYMENT = 'Payment',
    PAYOFF = 'PayOff',
}

interface IMakeComponentProps {
    setPaymentInfo: Dispatch<
        SetStateAction<{
            show: boolean;
            type: Tabs;
            paymentData: PaymentDataResponse;
        }>
    >;
}

const MakeComponent = ({ setPaymentInfo }: IMakeComponentProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as
        | { from?: string; accountNumber?: string }
        | undefined;
    const { payments } = useLocalization();
    const [active, setActive] = useState(
        state?.accountNumber ? Tabs.PAYOFF : Tabs.PAYMENT,
    );
    const [payment, setPayment] = useState({
        account: state?.accountNumber || '',
        errAccount: false,
        vin: '',
        errVin: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const editPaymentInfo = useSelector((state: IUser) => {
        return state.paymentInfo;
    });

    const editPaymentData = useSelector((state: IUser) => {
        return state.paymentData;
    });
    const [warninMessageKey, setWarninMessageKey] = useState('');

    const dealerId = useSelector((state: IUser) => {
        return state.currentDealers?.default;
    });
    const [brand, dealerName, dccNumber] = useSelector((state: IUser) => {
        return [
            state.dealerDataInfo?.dealer.brand ?? 'VW',
            state.dealerDataInfo?.dealer.dealerName ?? '',
            state.dealerDataInfo?.dealer.ccNumbers[0].toString() ?? '',
        ];
    });

    const handleChangeActive = (name: Tabs) => {
        setActive(name);
    };
    const handleSubmit = async () => {
        clearErrors();
        let errorAcc = null;
        let errorVIN = null;
        let searchValue = '';
        let searchType = null;
        const activeTab =
            active == Tabs.PAYMENT
                ? PaymentSource.Payment
                : PaymentSource.Payoff;
        if (payment.account.length) {
            if (
                payment.account.length < Number('9') ||
                payment.account.length > Number('14') ||
                new RegExp('/^\\d+$/').test(payment.account)
            ) {
                errorAcc = true;
                setPayment((oldValue) => {
                    return { ...oldValue, errAccount: true };
                });
            } else {
                errorAcc = false;
                setPayment((oldValue) => {
                    return { ...oldValue, errAccount: false };
                });
                searchValue = payment.account;
                searchType = PaymentDataSearhType.Account;
            }
        } else {
            errorAcc = false;
            setPayment((oldValue) => {
                return { ...oldValue, errAccount: false };
            });
        }

        if (payment.vin.length) {
            if (
                payment.vin.length != Number('17') ||
                new RegExp('[A-HJ-NPR-Z0-9]{17}').test(payment.vin) === false
            ) {
                errorVIN = true;
                setPayment((oldValue) => {
                    return { ...oldValue, errVin: true };
                });
            } else {
                errorVIN = false;
                setPayment((oldValue) => {
                    return { ...oldValue, errVin: false };
                });
                searchValue = payment.vin;
                searchType = PaymentDataSearhType.Vin;
            }
        } else {
            errorVIN = false;
            setPayment((oldValue) => {
                return { ...oldValue, errVin: false };
            });
        }

        if (
            errorAcc === false &&
            errorVIN === false &&
            dealerId &&
            searchType
        ) {
            setIsLoading(true);
            const service = new PaymentsService();
            const paymentSource = activeTab;
            try {
                const res = await service.paymentData(
                    dealerId,
                    searchType,
                    searchValue,
                    paymentSource,
                    brand,
                    dealerName,
                    dccNumber,
                );
                setIsLoading(false);
                setPaymentInfo({
                    show: true,
                    type: active,
                    paymentData: res.data,
                });
            } catch (error) {
                setIsLoading(false);
                setWarninMessageKey(getWarningMessageKey(error));
                setIsError(true);
            }
        }
    };

    useEffect(() => {
        if (state) {
            navigate(`${location.pathname}${location.search}${location.hash}`, {
                replace: true,
                state: null,
            });
        }
    }, []);

    useEffect(() => {
        if (editPaymentInfo) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    account: editPaymentInfo.AccountNumber,
                    vin: '',
                };
            });
            setActive(editPaymentInfo.TransactionType);
        }

        if (editPaymentData && editPaymentInfo) {
            setPaymentInfo({
                show: true,
                type: editPaymentInfo.TransactionType,
                paymentData: editPaymentData,
            });
        }
    }, []);

    const onChangeAccount = (event: any) => {
        const re = /^[0-9\b]+$/;

        // if value is not blank, then test the regex

        if (event.target.value === '' || re.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    account: event.target.value,
                    vin: '',
                };
            });
        }
    };

    const onChangeVin = (event: any) => {
        const re = /^[a-zA-Z0-9]+$/;

        if (event.target.value === '' || re.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    account: '',
                    vin: event.target.value.toLocaleUpperCase(),
                };
            });
        }
    };

    function clearErrors() {
        setIsError(false);
        setWarninMessageKey('');
    }

    return (
        <div className='dashboard-content-wrapper'>
            <div className='dashboard-content-container'>
                <div className='payments-wrapper'>
                    <section className='o-content-section  '>
                        {isError && (
                            <div className='u-mb-small'>
                                <WarningNotification
                                    type={NotificationType.ERROR}
                                    visibility={isError}
                                    setVisibility={setIsError}
                                    warningMessageKey={warninMessageKey}
                                />
                            </div>
                        )}
                        <div className=''>
                            <h5 className=''>
                                <span className='c-section-heading__title-text'>
                                    {payments.PAYMENT_MAKE_TITLE}
                                </span>
                            </h5>
                            <section className='description'>
                                {payments.PAYMENT_MAKE_DESCRIPTION}
                            </section>
                            <section className='c-form-section'>
                                <div className='c-form-section__content'>
                                    <div className='title u-mt-small'>
                                        <h6>
                                            <span className='c-section-heading__title-text'>
                                                {
                                                    payments.PAYMENT_MAKE_TITLEFORM
                                                }
                                            </span>
                                        </h6>
                                    </div>
                                    <div className='header-buttons u-mt-large'>
                                        <button
                                            className={`c-btn c-btn--secondary ${
                                                active === Tabs.PAYMENT
                                                    ? 'active'
                                                    : 'inactive'
                                            }`}
                                            type='button'
                                            onClick={() => {
                                                handleChangeActive(
                                                    Tabs.PAYMENT,
                                                );
                                            }}
                                        >
                                            <span className='c-btn__text'>
                                                {
                                                    payments.PAYMENT_MAKE_PAYMENTBTN
                                                }
                                            </span>
                                        </button>
                                        <button
                                            className={`c-btn c-btn--secondary ${
                                                active === Tabs.PAYOFF
                                                    ? 'active'
                                                    : 'inactive'
                                            }`}
                                            type='button'
                                            onClick={() => {
                                                handleChangeActive(Tabs.PAYOFF);
                                            }}
                                        >
                                            <span className='c-btn__text'>
                                                {
                                                    payments.PAYMENT_MAKE_PAYOFFBTN
                                                }
                                            </span>
                                        </button>
                                    </div>
                                    <div className='o-fieldset__row u-mt-large u-mb-large'>
                                        <div className='o-layout'>
                                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                                <div className='c-form-field'>
                                                    <div className='c-form-field__box'>
                                                        <div>
                                                            <label
                                                                htmlFor='m1l7e063'
                                                                className='c-form-field__label u-mr-xsmall'
                                                            >
                                                                {
                                                                    payments.PAYMENT_MAKE_ACCOUNTLABEL
                                                                }
                                                            </label>
                                                            <TooltipIcon
                                                                text={
                                                                    payments.PAYMENT_MAKE_ACCOUNTTIP
                                                                }
                                                            ></TooltipIcon>
                                                        </div>
                                                        <div className='c-input'>
                                                            <input
                                                                className={`c-input__input ${
                                                                    payment.errAccount
                                                                        ? 'is-error'
                                                                        : ''
                                                                }`}
                                                                onChange={
                                                                    onChangeAccount
                                                                }
                                                                type='text'
                                                                value={
                                                                    payment.account
                                                                }
                                                                minLength={9}
                                                                maxLength={14}
                                                            />
                                                        </div>
                                                        <div>
                                                            {payment.errAccount ? (
                                                                <span
                                                                    className={
                                                                        'c-error-message'
                                                                    }
                                                                    style={{
                                                                        position:
                                                                            'absolute',
                                                                    }}
                                                                >
                                                                    {
                                                                        payments.PAYMENT_MAKE_ACCOUNTERROR
                                                                    }
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='o-fieldset__row u-mt-large'>
                                        <div className='o-layout'>
                                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                                <div className='c-form-field'>
                                                    <div className='c-form-field__box'>
                                                        <div>
                                                            <label
                                                                htmlFor='m1l7e063'
                                                                className='c-form-field__label u-mr-xsmall'
                                                            >
                                                                {
                                                                    payments.PAYMENT_MAKE_VINLABEL
                                                                }
                                                            </label>
                                                            <TooltipIcon
                                                                text={
                                                                    payments.PAYMENT_MAKE_VINTIP
                                                                }
                                                            ></TooltipIcon>
                                                        </div>
                                                        <div className='c-input'>
                                                            <input
                                                                className={`c-input__input ${
                                                                    payment.errVin
                                                                        ? 'is-error'
                                                                        : ''
                                                                }`}
                                                                onChange={
                                                                    onChangeVin
                                                                }
                                                                type='text'
                                                                value={
                                                                    payment.vin
                                                                }
                                                                minLength={17}
                                                                maxLength={17}
                                                            />
                                                        </div>
                                                        <div>
                                                            {payment.errVin ? (
                                                                <span
                                                                    className={
                                                                        'c-error-message'
                                                                    }
                                                                    style={{
                                                                        position:
                                                                            'absolute',
                                                                    }}
                                                                >
                                                                    {
                                                                        payments.PAYMENT_MAKE_VINERROR
                                                                    }
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='o-layout__item  u-1/1  u-1/1@xs'>
                                                <div className='o-fieldset__row button-group'>
                                                    <button
                                                        className={`c-btn ${
                                                            payment.account
                                                                .length ===
                                                                Number('0') &&
                                                            payment.vin
                                                                .length ===
                                                                Number('0')
                                                                ? 'btn-secondary'
                                                                : ''
                                                        }`}
                                                        type='button'
                                                        onClick={handleSubmit}
                                                        disabled={
                                                            (payment.account
                                                                .length ===
                                                                Number('0') &&
                                                                payment.vin
                                                                    .length ===
                                                                    Number(
                                                                        '0',
                                                                    )) ||
                                                            isLoading
                                                        }
                                                    >
                                                        <span className='c-btn__text'>
                                                            {isLoading ? (
                                                                <Spinner />
                                                            ) : (
                                                                payments.PAYMENT_MAKE_SUBMIT
                                                            )}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </section>
                    <section className='privacy-footer-container '>
                        <FooterPrivacy />
                    </section>
                </div>
            </div>
        </div>
    );
};

const MakePaymentView = () => {
    const [paymentInfo, setPaymentInfo] = useState({
        show: false,
        type: Tabs.PAYMENT,
        paymentData: {
            accountDetail: {
                accountNumber: '',
                country: Country.Canada,
                customerName: '',
                vehicleMake: '',
                vehicleModel: '',
                vehicleYear: '',
                bodyStyle: '',
                maturityDate: '',
                accountType: AccountType.Unknown,
                accountStatus: '',
                address: {
                    StateOrProvince: '',
                    TypeCode: '',
                },
                prePayLease: false,
                singlePayLease: false,
                repossessed: false,
                vin: '',
                processStatus: '',
                TermTypeStatus: '',
                balloonFinanceType: '',
                paymentSource: '',
                contractTerm: 0,
                partyId: 0,
            },
            privacyFlag: false,
            isBrassHatDealer: false,
            bankAccountDetails: {
                ccNumber: 0,
                accountNumber: '',
                routingNumber: '',
                accountType: 0,
            },
        },
    });

    return paymentInfo.show ? (
        <PaymentInformation
            setPaymentInfo={setPaymentInfo}
            paymentData={paymentInfo.paymentData}
            type={paymentInfo.type}
        />
    ) : (
        <MakeComponent setPaymentInfo={setPaymentInfo} />
    );
};

export default MakePaymentView;
